import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import axios from "axios";

/**
 * PayInvoicePage — /pay/:iid
 *
 * Public (no-auth) branded checkout page for any invoice provider.
 * - Razorpay (INR): opens Razorpay Checkout.js modal, verifies signature.
 * - Stripe (everything else): redirects to a Stripe Checkout Session, and
 *   verifies the returned `?stripe_session_id=…` against the server so
 *   we don't rely on a webhook to mark the invoice paid.
 *
 * Same layout for both providers so customers never see two different
 * experiences depending on which country they're paying from.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PayInvoicePage() {
  const { iid } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");

  const stripeSessionId = params.get("stripe_session_id");
  const cancelled = params.get("cancelled") === "1";

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/public/invoices/${iid}/checkout`);
      setD(r.data);
      if (r.data.paid) setPaid(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Invoice not found");
    }
  };

  useEffect(() => {
    document.title = "Pay invoice — WebsiteTech";
    refresh();
  }, [iid]);

  // Handle Stripe redirect-back: verify the session server-side.
  useEffect(() => {
    if (!stripeSessionId || paid) return;
    (async () => {
      try {
        const r = await axios.post(
          `${API}/public/invoices/${iid}/verify-stripe`,
          { session_id: stripeSessionId },
        );
        if (r.data.ok) {
          setPaid(true);
        } else {
          setVerifyMsg(
            "Stripe reported the payment as "
            + (r.data.payment_status || "not yet complete")
            + ". You can retry the payment below.");
          await refresh();
        }
      } catch (e) {
        setVerifyMsg(
          "We couldn't verify the payment (" +
          (e?.response?.data?.detail || "invalid session") +
          "). Please retry below or contact support.");
        await refresh();
      }
    })();
  }, [stripeSessionId, iid]);

  const openRazorpay = async () => {
    const ok = await loadRazorpay();
    if (!ok) { setErr("Payment SDK failed to load"); return; }
    setBusy(true);
    const opts = {
      key: d.razorpay_key_id,
      amount: d.amount_paise,
      currency: "INR",
      order_id: d.razorpay_order_id,
      name: "WebsiteTech.pro",
      description: `${d.number} — ${d.title}`,
      prefill: {
        name: d.customer_name || "",
        email: d.customer_email || "",
      },
      theme: { color: "#EA580C" },
      handler: async (resp) => {
        try {
          await axios.post(`${API}/public/invoices/${iid}/verify-razorpay`, {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setPaid(true);
        } catch (e) {
          setErr(e?.response?.data?.detail || "Verification failed");
        } finally { setBusy(false); }
      },
      modal: { ondismiss: () => setBusy(false) },
    };
    const rzp = new window.Razorpay(opts);
    rzp.open();
  };

  const openStripe = () => {
    if (d.stripe_checkout_url) {
      window.location.href = d.stripe_checkout_url;
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md border border-rose-200 bg-rose-50 p-6 text-center"
              data-testid="pay-error">
          <div className="text-sm text-rose-800">{err}</div>
        </Card>
      </div>
    );
  }
  if (!d) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-12"
         data-testid="pay-page">
      <Card className="w-full max-w-md border border-slate-200 bg-white p-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-orange-600">
          Invoice · {d.number}
        </div>
        <h1 className="mt-1 font-heading text-2xl font-black text-slate-900">{d.title}</h1>
        {d.customer_name && (
          <div className="mt-1 text-sm text-slate-600">for {d.customer_name}</div>
        )}

        <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4 text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500">Amount due</div>
          <div className="mt-1 font-heading text-4xl font-black text-slate-900" data-testid="pay-amount">
            {d.currency} {Number(d.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>

        {cancelled && !paid && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            No charge yet — you can pay whenever you're ready.
          </div>
        )}
        {verifyMsg && !paid && (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
               data-testid="pay-verify-error">
            {verifyMsg}
          </div>
        )}

        {paid ? (
          <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-center text-emerald-800"
               data-testid="pay-success">
            <CheckCircle2 className="mx-auto h-8 w-8" />
            <div className="mt-1 font-semibold">Payment received — thank you!</div>
            <div className="mt-1 text-xs text-emerald-700">
              A receipt has been logged against invoice {d.number}.
            </div>
          </div>
        ) : d.provider === "razorpay" ? (
          <Button
            className="mt-6 w-full bg-orange-600 text-white hover:bg-orange-700"
            onClick={openRazorpay} disabled={busy}
            data-testid="pay-razorpay-btn"
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Pay with Razorpay
          </Button>
        ) : d.provider === "stripe" && d.stripe_checkout_url ? (
          <Button
            className="mt-6 w-full bg-orange-600 text-white hover:bg-orange-700"
            onClick={openStripe}
            data-testid="pay-stripe-btn"
          >
            Pay with Stripe <ExternalLink className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            No payment method is configured for this invoice yet.
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">
          Secure checkout · Powered by WebsiteTech.pro
        </p>
      </Card>
    </div>
  );
}
